// 季節別館（拡張島）の「軽い来客演出」（v0.8.5、Stage 2）。
//
// 経済（季節島がその季節の常連を呼ぶ）は sim 側で実装済み。ここはその見た目だけ:
// 表示中の拡張島に、その島の季節を好む客のブロブを数体、ゆっくり巡回させて「別館にも
// 常連が通っている」雰囲気を出す。ホーム島の本格的な動線（入口→カフェ→席）とは別の、
// 純粋に装飾的な ambient 表示（経済には一切影響しない）。
//
// root は diorama.island の子にする（カルーセルと一緒に自転する＝島ローカル空間）。

import * as THREE from 'three';
import { buildCustomerMesh } from './customer_mesh';
import { GARDEN_Y, gridToWorld } from './diorama';
import { GRID_SIZE, isPlaceableCell, shapeForIslandIndex, twinSurfaceY } from '../sim/garden';
import { islandSeason } from '../sim/season';
import { CUSTOMER_CATALOG } from '../sim/customers';

/** 同時に出す ambient 客の上限。 */
const MAX_VISITORS = 4;
/** ambient 客のスケール（控えめに少し小さく）。 */
const AMBIENT_SCALE = 0.85;
/** homeセル周りをゆっくり巡回する半径（セル幅 0.8 より小さく＝島からはみ出しにくい）。 */
const WANDER_RADIUS = 0.34;

interface AmbientVisitor {
  group: THREE.Group;
  /** 巡回の中心（島ローカル XZ）。 */
  cx: number;
  cz: number;
  phase: number;
  /** 巡回の角速度。 */
  speed: number;
  /** twin 島なら高低差を反映する。 */
  isTwin: boolean;
}

export interface IslandVisitors {
  /** diorama.island に add する表示ルート。 */
  root: THREE.Group;
  /** 表示島を設定（拡張島なら季節客を配置、ホーム/無効なら何も出さない）。fillCount=配置デコ数。 */
  setIsland(islandIndex: number, fillCount: number): void;
  /** 毎フレームの巡回アニメ。 */
  update(tSec: number): void;
  /** 全 ambient 客を消す。 */
  clear(): void;
  dispose(): void;
}

function disposeGroup(g: THREE.Object3D): void {
  g.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
    const mat = m.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
    else mat?.dispose();
  });
}

export function createIslandVisitors(): IslandVisitors {
  const root = new THREE.Group();
  let visitors: AmbientVisitor[] = [];

  function clear(): void {
    for (const v of visitors) {
      root.remove(v.group);
      disposeGroup(v.group);
    }
    visitors = [];
  }

  /** その島の配置可能セル（＝島の上面に乗るセル）を列挙。 */
  function validCells(islandIndex: number): { gx: number; gy: number }[] {
    const out: { gx: number; gy: number }[] = [];
    for (let gy = 0; gy < GRID_SIZE; gy++) {
      for (let gx = 0; gx < GRID_SIZE; gx++) {
        if (isPlaceableCell(gx, gy, islandIndex)) out.push({ gx, gy });
      }
    }
    return out;
  }

  function setIsland(islandIndex: number, fillCount: number): void {
    clear();
    const season = islandSeason(islandIndex);
    if (!season) return; // ホーム島(0)・無効は ambient なし（ホームは customer_view が担当）。
    const seasonal = CUSTOMER_CATALOG.filter((c) => c.favoriteSeason === season);
    if (seasonal.length === 0) return;
    const cells = validCells(islandIndex);
    if (cells.length === 0) return;
    const isTwin = shapeForIslandIndex(islandIndex) === 'twin';
    // 庭が育つほど賑わう（1..MAX）。空でも 1 体は通う（別館の常連）。
    const n = Math.max(1, Math.min(MAX_VISITORS, 1 + Math.floor(fillCount / 6)));
    for (let i = 0; i < n; i++) {
      const def = seasonal[i % seasonal.length];
      // セルを決定的に散らして選ぶ（島ごと・個体ごとに違う場所に）。
      const cell = cells[(i * 7 + islandIndex * 3 + 1) % cells.length];
      const w = gridToWorld(cell.gx, cell.gy);
      const g = buildCustomerMesh(def.look);
      g.scale.multiplyScalar(AMBIENT_SCALE);
      root.add(g);
      visitors.push({
        group: g,
        cx: w.x,
        cz: w.z,
        phase: i * 1.7,
        speed: 0.16 + 0.03 * i,
        isTwin,
      });
    }
  }

  function update(tSec: number): void {
    for (const v of visitors) {
      const a = tSec * v.speed + v.phase;
      const x = v.cx + Math.cos(a) * WANDER_RADIUS;
      const z = v.cz + Math.sin(a) * WANDER_RADIUS;
      const bob = Math.sin(tSec * 2.5 + v.phase * 6.28) * 0.03;
      const surf = v.isTwin ? GARDEN_Y + twinSurfaceY(x) : GARDEN_Y;
      v.group.position.set(x, surf + bob, z);
      // 進行方向（巡回の接線）を向く。
      v.group.rotation.y = Math.atan2(-Math.sin(a), Math.cos(a)) + Math.PI / 2;
    }
  }

  return { root, setIsland, update, clear, dispose: clear };
}
